import { makeAutoObservable } from "mobx";
import { v4 as uuidv4 } from "uuid";
import { getMondayOfWeek } from "../../utils/dateUtils";
import { formatNumber2Dec } from "../../utils/numberUtils";
import Colors from "../../../Resources/Color";
import {
  METRIC_OPTIONS,
  defaultMetricByType,
  filterMetricOptions,
  getMetricValue,
  resolveSelectedMetricOptions,
  sanitizeMetricByType,
} from "./statisticsMetrics";

const MAX_BLOCKS = 3;
const MAX_TRACKS = 3;

// Reuse existing theme tokens only (no new hard-coded colors).
// This palette is used for deterministic mapping: (exerciseDisplayName + protocolSlot) -> color.
const STATISTICS_SERIES_COLOR_SCALE = [
  Colors.bubbleColor,
  Colors.bodyweightColor,
  Colors.tracColor,
  Colors.noteColor,
  Colors.competitionColor,
  Colors.grayColor,
  Colors.darkgrayColor,
  Colors.blackColor,
  "#0047FF",
  "#FF2D95",
  "#00E5FF",
].filter(Boolean);

/**
 * Owns all selection state + derived data for the redesigned Statistics experience.
 * Implementation intentionally left minimal for now.
 */
export class ClientStatisticsStore {
  client = null;

  // Internal cache to ensure we don't assign the same palette index to multiple
  // slot-split series within the current selection state.
  _seriesColorCacheKey = "";
  _seriesColorIndexByKey = new Map();

  selectedBlockIds = [];

  // A "track" is one line/legend item to compare.
  // For now, we match exercises by exact `exercise.getName(t, language)` string.
  tracks = [];

  // Names the user explicitly picked via search; used to keep them visible
  // when still allowed by the current block intersection or time mode.
  userPinnedExerciseDisplayNames = [];

  startWeek = 1;
  endWeek = 1;
  // Time-mode default: include all historical data until now.
  startDate = new Date(0);
  endDate = new Date();

  // Chart/table metric selection (shared across the view for consistency).
  metricByType = defaultMetricByType(this.allowStressIndex);

  // Prevent repeated first-time auto selections per client/session.
  hasAppliedInitialSuggestion = false;

  searchTerm = "";
  isSearchOpen = false;

  // Latest debug payload to show on screen + copy.
  lastDebugEvent = null;

  constructor({ client } = {}) {
    this.client = client ?? null;
    makeAutoObservable(this);
  }

  get allowStressIndex() {
    return !this?.client?.coach?.rirOrRpe;
  }

  get metricOptions() {
    return filterMetricOptions(this.allowStressIndex, METRIC_OPTIONS);
  }

  get resolvedMetricByType() {
    return sanitizeMetricByType(this.metricByType, {
      allowStressIndex: this.allowStressIndex,
      metricOptions: this.metricOptions,
    });
  }

  setMetricByType(next) {
    const current =
      this.metricByType ?? defaultMetricByType(this.allowStressIndex);
    const merged =
      typeof next === "function"
        ? next(current)
        : { ...current, ...(next ?? {}) };

    this.metricByType = sanitizeMetricByType(merged, {
      allowStressIndex: this.allowStressIndex,
      metricOptions: this.metricOptions,
    });
  }

  getSelectedMetricOptions() {
    return resolveSelectedMetricOptions(this.metricByType, {
      allowStressIndex: this.allowStressIndex,
      metricOptions: this.metricOptions,
    });
  }

  _hashStringToInt(value) {
    // Simple deterministic hash (djb2-ish). Good enough for stable palette indexing.
    const str = String(value ?? "");
    let hash = 5381;
    for (let i = 0; i < str.length; i++) {
      hash = (hash * 33) ^ str.charCodeAt(i);
    }
    // Force positive 32-bit
    return hash >>> 0;
  }

  getColorForExerciseAndSlot(exerciseDisplayName, protocolSlot, options = {}) {
    const palette = STATISTICS_SERIES_COLOR_SCALE;
    if (!palette || palette.length === 0) return Colors.bubbleColor;

    const forceSlotVariant = Boolean(options.forceSlotVariant);
    const identitySalt = options.identitySalt
      ? String(options.identitySalt)
      : "";

    // Reset per-selection cache when tracks change.
    // Keep it stable within a selection so slot-split series don't collide.
    try {
      const cacheKey = JSON.stringify({
        paletteLen: palette.length,
        tracks: (this.tracks ?? []).map((t) => ({
          exerciseDisplayName: t?.exerciseDisplayName ?? "",
          slotMode: t?.slotMode ?? "any",
          protocolSlot: t?.protocolSlot ?? null,
        })),
      });
      if (cacheKey !== this._seriesColorCacheKey) {
        this._seriesColorCacheKey = cacheKey;
        this._seriesColorIndexByKey = new Map();
      }
    } catch (e) {
      // ignore cache failures
    }

    // Find which track this belongs to by exerciseDisplayName
    const trackIndex = (this.tracks ?? []).findIndex(
      (t) => (t?.exerciseDisplayName ?? "") === exerciseDisplayName
    );

    if (trackIndex < 0) {
      // Not in tracks array, fallback to hash-based color
      const key = `${String(exerciseDisplayName ?? "")}::${String(
        protocolSlot ?? "none"
      )}::${identitySalt}`;
      const idx = this._hashStringToInt(key) % palette.length;
      return palette[idx];
    }

    // Use track array position to determine base color
    // First track (index 0) → palette[0] (bubbleColor)
    // Second track (index 1) → palette[1], etc.
    // This ensures stable colors that don't change when adding/removing other tracks
    const baseColorIndex = trackIndex % palette.length;

    // When comparing multiple tracks, default to the base track color.
    // Slot-specific colors are only used when we explicitly opt-in (slot-split).
    if (!forceSlotVariant && (this.tracks?.length ?? 0) > 1) {
      return palette[baseColorIndex];
    }

    const track = (this.tracks ?? [])[trackIndex] ?? null;
    const slotMode = track?.slotMode ?? "any";

    // Non-split series should always use the base color.
    // Slot-specific colors are only used when a track is intentionally split
    // into multiple protocolSlot series (slotMode === "any" AND forceSlotVariant).
    if (!protocolSlot || slotMode === "exact" || !forceSlotVariant) {
      return palette[baseColorIndex];
    }

    // Slot-split (single track): assign a stable, unique palette index per
    // (exerciseDisplayName, protocolSlot) within the current selection.
    const seriesKey = `${String(exerciseDisplayName ?? "")}::${String(
      protocolSlot ?? "none"
    )}::${identitySalt}`;

    const cachedIdx = this._seriesColorIndexByKey?.get?.(seriesKey);
    if (typeof cachedIdx === "number" && cachedIdx >= 0) {
      return palette[cachedIdx] ?? Colors.bubbleColor;
    }

    // Reserve base indices for the current tracks (even though we only have 1
    // track here, keeping it generic is safer).
    const reservedBaseIndices = new Set(
      (this.tracks ?? []).map((_, idx) => idx % palette.length)
    );

    const usedIndices = new Set([
      ...reservedBaseIndices,
      ...Array.from(this._seriesColorIndexByKey?.values?.() ?? []),
    ]);

    let candidateIdx =
      this._hashStringToInt(seriesKey) % Math.max(1, palette.length);
    let attempts = 0;
    while (usedIndices.has(candidateIdx) && attempts < palette.length) {
      candidateIdx = (candidateIdx + 1) % palette.length;
      attempts++;
    }

    try {
      this._seriesColorIndexByKey?.set?.(seriesKey, candidateIdx);
    } catch (e) {
      // ignore cache set failures
    }

    return palette[candidateIdx] ?? Colors.bubbleColor;
  }

  getColorForBlock(blockId) {
    const palette = STATISTICS_SERIES_COLOR_SCALE;
    if (!palette || palette.length === 0) return Colors.bubbleColor;
    const key = `block::${String(blockId ?? "")}`;
    const idx = this._hashStringToInt(key) % palette.length;
    return palette[idx];
  }

  _formatSeriesLabel({ t, exerciseDisplayName, protocolSlot }) {
    const base = String(exerciseDisplayName ?? "");
    if (!protocolSlot) return base;
    try {
      const slotLabel = t ? t(protocolSlot) : String(protocolSlot);
      return `${base} (${slotLabel})`;
    } catch (e) {
      return `${base} (${String(protocolSlot)})`;
    }
  }

  _logDebug(action, ctx = {}) {
    return; // Disable debug logging for now
    try {
      const ts = new Date().toISOString();
      // Keep logs skimmable while still copy/paste friendly for chat.
      console.groupCollapsed(`[StatisticsDebug] ${action} @ ${ts}`);
      console.log("snapshot", this.debugSnapshot);

      const t = ctx?.t;
      const language = ctx?.language;
      const snapshot = this.debugSnapshot;

      let occurrencesCount = null;
      let occurrencesSample = [];
      let trackSummaries = [];
      let trackOccurrencesSummary = [];

      if (t && language) {
        const trackNames = (this.tracks ?? [])
          .map((tr) => tr?.exerciseDisplayName)
          .filter(Boolean);

        // Keep occurrences focused on what the UI will actually chart/table.
        const occurrences = this.getOccurrences({
          t,
          language,
          exerciseDisplayNameFilter:
            trackNames.length > 0 ? new Set(trackNames) : null,
        });
        occurrencesCount = occurrences.length;
        occurrencesSample = occurrences.slice(0, 5);

        trackOccurrencesSummary = (this.tracks ?? []).map((tr) => {
          const matchName = tr?.exerciseDisplayName ?? "";
          const byName = (occurrences ?? []).filter(
            (o) => (o?.exerciseDisplayName ?? "") === matchName
          );

          const slotMode = tr?.slotMode ?? "any";
          const protocolSlot = tr?.protocolSlot ?? null;
          let byNameAndSlot = byName;
          if (slotMode === "exact") {
            if (!protocolSlot) {
              byNameAndSlot = [];
            } else {
              byNameAndSlot = byName.filter(
                (o) => (o?.protocolSlot ?? null) === protocolSlot
              );
            }
          }

          return {
            trackId: tr?.id,
            exerciseDisplayName: matchName,
            slotMode,
            protocolSlot,
            occurrencesMatchingNameCount: byName.length,
            occurrencesMatchingNameSample: byName.slice(0, 3),
            occurrencesAfterSlotFilterCount: byNameAndSlot.length,
            occurrencesAfterSlotFilterSample: byNameAndSlot.slice(0, 3),
          };
        });

        trackSummaries = (this.tracks ?? []).map((tr) => {
          const points = this._getTrackPointsFromOccurrences(tr, occurrences);
          const countsByX = points.reduce((acc, p) => {
            const key = String(p.x);
            acc[key] = (acc[key] ?? 0) + 1;
            return acc;
          }, {});
          const duplicateX = Object.entries(countsByX)
            .filter(([, c]) => c > 1)
            .slice(0, 5);

          return {
            trackId: tr.id,
            exerciseDisplayName: tr.exerciseDisplayName,
            slotMode: tr.slotMode ?? "any",
            protocolSlot: tr.protocolSlot ?? null,
            pointsCount: points.length,
            duplicateX,
            pointsSample: points.slice(0, 3),
          };
        });

        console.log("occurrences.length", occurrencesCount);
        console.log("occurrences.sample", occurrencesSample);
        console.log("tracks.occurrencesSummary", trackOccurrencesSummary);
        console.log("tracks.pointsSummary", trackSummaries);
      } else {
        console.log("note", "Missing {t, language} for debug payload.");
      }

      this.lastDebugEvent = {
        action,
        ts,
        snapshot,
        occurrencesCount,
        occurrencesSample,
        tracksOccurrencesSummary: trackOccurrencesSummary,
        tracksPointsSummary: trackSummaries,
      };

      console.groupEnd();
    } catch (e) {
      console.warn("[StatisticsDebug] logging failed", e);
    }
  }

  get lastDebugText() {
    try {
      return this.lastDebugEvent
        ? JSON.stringify(this.lastDebugEvent, null, 2)
        : "";
    } catch (e) {
      return "";
    }
  }

  setClient(client) {
    this.client = client;
  }

  setSearchOpen(open) {
    this.isSearchOpen = open;
  }

  setSearchTerm(term) {
    this.searchTerm = term;
  }

  markInitialSuggestionApplied() {
    this.hasAppliedInitialSuggestion = true;
  }

  setSelectedBlockIds(blockIds) {
    this.selectedBlockIds = Array.isArray(blockIds) ? blockIds : [];
  }

  setTracks(tracks) {
    this.tracks = Array.isArray(tracks) ? tracks : [];
  }

  setWeekRange({ startWeek, endWeek } = {}, ctx) {
    const maxWeek = this.getMaxWeeksAcrossSelectedBlocks();
    const maxWeekSafe = Math.max(1, Number(maxWeek ?? 1) || 1);

    let start = Number(startWeek ?? this.startWeek ?? 1);
    let end = Number(endWeek ?? this.endWeek ?? 1);

    if (Number.isNaN(start)) start = 1;
    if (Number.isNaN(end)) end = maxWeekSafe;

    start = Math.min(Math.max(1, start), maxWeekSafe);
    end = Math.min(Math.max(1, end), maxWeekSafe);

    if (start > end) start = end;

    this.startWeek = start;
    this.endWeek = end;
    this._logDebug("setWeekRange", ctx);
  }

  setDateRange({ startDate, endDate } = {}, ctx) {
    const coerceDate = (value) => {
      if (!value) return null;
      if (value instanceof Date) return value;
      if (typeof value?.toDate === "function") {
        try {
          return value.toDate();
        } catch (e) {
          return null;
        }
      }
      const d = new Date(value);
      return isNaN(d.getTime()) ? null : d;
    };

    const start = coerceDate(startDate) ?? new Date(0);
    const end = coerceDate(endDate) ?? new Date();

    if (start.getTime() > end.getTime()) {
      this.startDate = new Date(end);
      this.endDate = new Date(start);
    } else {
      this.startDate = new Date(start);
      this.endDate = new Date(end);
    }

    this._logDebug("setDateRange", ctx);
  }

  // Placeholder APIs for later implementation
  get mode() {
    return this.selectedBlockIds.length > 0 ? "weeks" : "time";
  }

  get activeBlockId() {
    return this.selectedBlockIds.length > 0 ? this.selectedBlockIds[0] : null;
  }

  get maxBlocksAllowed() {
    return this.tracks.length > 1 ? 1 : MAX_BLOCKS;
  }

  get maxTracksAllowed() {
    return this.selectedBlockIds.length > 1 ? 1 : MAX_TRACKS;
  }

  get canAddBlock() {
    // In single-block mode (multi-track), users can still *change* the selected
    // block (swap), even if they cannot increase the count.
    if (this.maxBlocksAllowed === 1) return true;
    return this.selectedBlockIds.length < this.maxBlocksAllowed;
  }

  get canAddTrack() {
    // In single-track mode (multi-block), users can still *change* the selected
    // exercise (swap), even if they cannot increase the count.
    if (this.maxTracksAllowed === 1) return true;
    return this.tracks.length < this.maxTracksAllowed;
  }

  _setSingleSelectedBlockId(blockId, ctx, reason) {
    if (!blockId) return;
    this.selectedBlockIds = [blockId];
    this.startWeek = 1;
    this.endWeek = this.getMaxWeeksAcrossSelectedBlocks();
    this._logDebug(reason ?? "setSingleSelectedBlockId", ctx);
  }

  _replaceTracksWithSingleDisplayName(exerciseDisplayName, ctx, reason) {
    if (!exerciseDisplayName) return null;
    const newId = uuidv4();
    this.tracks = [
      {
        id: newId,
        exerciseDisplayName,
        slotMode: "any",
        protocolSlot: null,
      },
    ];
    this._maybePinUserExerciseDisplayName(exerciseDisplayName, ctx);
    this._logDebug(reason ?? "replaceTracksWithSingle", ctx);
    return newId;
  }

  get availableBlocks() {
    const blocks = Array.isArray(this.client?.blocks) ? this.client.blocks : [];
    return blocks;
  }

  getBlockById(blockId) {
    if (!blockId) return null;
    return this.availableBlocks.find((b) => b?.id === blockId) ?? null;
  }

  get selectedBlocks() {
    const selected = new Set(this.selectedBlockIds);
    return this.availableBlocks.filter((b) => selected.has(b?.id));
  }

  getSuggestedExerciseDisplayNames(t, language) {
    // Suggestion rules (shared by header chips + floating picker):
    // 1) If blocks selected → derive from those blocks; else pick the most recent block that has workouts with exercises.
    // 2) Only consider exercises that have recorded data (hasSeriesWithData true) in at least one workout.
    // 3) If every selected block has protocol-slot exercises, show the intersection of names that have protocolSlot !== null across all selected blocks.
    // 4) Otherwise, rank by frequency of being in the first two exercises of each workout, but only keep exercises present in every selected block.
    // 5) Exclude already-selected tracks and cap to 20 suggestions.
    try {
      const exclude = new Set(
        (this.tracks ?? []).map((tr) => tr?.exerciseDisplayName).filter(Boolean)
      );

      const selectedBlocks = this.selectedBlocks;
      let suggestions = [];

      if (selectedBlocks.length > 0) {
        suggestions = this._suggestExercisesFromBlocks(selectedBlocks, {
          t,
          language,
          exclude,
        });
      } else {
        const block = this._getMostRecentBlockWithExercises({ t, language });
        if (block) {
          suggestions = this._suggestExercisesFromBlocks([block], {
            t,
            language,
            exclude,
          });
        }
      }

      const pinned = this._getPinnedExercisesVisibleInScope({
        t,
        language,
        exclude,
      });

      const seen = new Set();
      const merged = [];

      [...pinned, ...suggestions].forEach((name) => {
        if (!name) return;
        if (seen.has(name)) return;
        seen.add(name);
        merged.push(name);
      });

      return merged.slice(0, 15);
    } catch (e) {
      console.warn("getSuggestedExerciseDisplayNames failed", e);
      return [];
    }
  }

  getSuggestedProtocolSlotExerciseDisplayNames(t, language) {
    // Deprecated: kept for backward compatibility. Use getSuggestedExerciseDisplayNames instead.
    return this.getSuggestedExerciseDisplayNames(t, language);
  }

  _getMostRecentBlockWithExercises({ t, language } = {}) {
    const coerceDate = (value) => {
      if (!value) return null;
      if (value instanceof Date) return value;
      if (typeof value?.toDate === "function") {
        try {
          return value.toDate();
        } catch (e) {
          return null;
        }
      }
      const d = new Date(value);
      return isNaN(d.getTime()) ? null : d;
    };

    let best = null;
    let bestDateMs = -Infinity;

    (this.availableBlocks ?? []).forEach((block) => {
      const workouts = Array.isArray(block?.workouts) ? block.workouts : [];
      let hasExercises = false;
      let latest = -Infinity;

      workouts.forEach((w) => {
        const exercises = Array.isArray(w?.exercises) ? w.exercises : [];
        if (exercises.length > 0) {
          hasExercises = true;
        }
        const d = coerceDate(w?.date);
        if (d && d.getTime() > latest) {
          latest = d.getTime();
        }
      });

      if (!hasExercises) return;
      if (latest > bestDateMs) {
        bestDateMs = latest;
        best = block;
      }
    });

    return best;
  }

  _getBlockLatestDateMs(block) {
    const coerceDate = (value) => {
      if (!value) return null;
      if (value instanceof Date) return value;
      if (typeof value?.toDate === "function") {
        try {
          return value.toDate();
        } catch (e) {
          return null;
        }
      }
      const d = new Date(value);
      return isNaN(d.getTime()) ? null : d;
    };

    let latest = -Infinity;
    const workouts = Array.isArray(block?.workouts) ? block.workouts : [];
    workouts.forEach((w) => {
      const d = coerceDate(w?.date);
      if (d && d.getTime() > latest) {
        latest = d.getTime();
      }
    });
    return latest;
  }

  getMostRecentEligibleBlockId({ t, language } = {}) {
    const eligible = this.getEligibleBlockIdsForCurrentTracks({ t, language });
    if (!eligible || eligible.size === 0) return null;

    let bestId = null;
    let bestDate = -Infinity;

    (this.availableBlocks ?? []).forEach((block) => {
      const blockId = block?.id ?? null;
      if (!blockId || !eligible.has(blockId)) return;

      const latest = this._getBlockLatestDateMs(block);
      if (latest > bestDate) {
        bestDate = latest;
        bestId = blockId;
      }
    });

    return bestId;
  }

  _getExerciseDisplayName(exercise, t, language) {
    try {
      if (typeof exercise?.getName === "function") {
        return exercise.getName(t, language);
      }
    } catch (e) {
      // ignore
    }
    return String(exercise?.name ?? "");
  }

  _collectBlockExerciseStats(block, { t, language } = {}) {
    const workouts = Array.isArray(block?.workouts) ? block.workouts : [];
    const slotNames = new Set();
    const freq = new Map();
    const allNames = new Set();
    let hasSlot = false;

    workouts.forEach((workout) => {
      const exercises = Array.isArray(workout?.exercises)
        ? workout.exercises
        : [];

      exercises.forEach((ex, idx) => {
        const name = this._getExerciseDisplayName(ex, t, language);
        if (!name) return;

        // Only consider exercises that actually have recorded data.
        if (typeof ex?.hasSeriesWithData === "boolean") {
          if (!ex.hasSeriesWithData) return;
        } else if (typeof ex?.hasSeriesWithData === "function") {
          if (!ex.hasSeriesWithData()) return;
        }

        allNames.add(name);

        const slot = ex?.protocolSlot ?? null;
        if (slot != null) {
          hasSlot = true;
          slotNames.add(name);
        }

        // Count only the first two exercises of each workout for fallback ranking.
        if (idx < 2) {
          const current = freq.get(name) ?? 0;
          freq.set(name, current + 1);
        }
      });
    });

    return { hasSlot, slotNames, freq, allNames };
  }

  _getExercisesIntersectionAcrossBlocks(blocks, { t, language } = {}) {
    if (!blocks || blocks.length === 0) return new Set();

    const stats = blocks.map((b) =>
      this._collectBlockExerciseStats(b, { t, language })
    );

    let intersection = new Set(stats[0].allNames ?? []);
    stats.slice(1).forEach((s) => {
      const next = new Set();
      (s.allNames ?? []).forEach((name) => {
        if (intersection.has(name)) next.add(name);
      });
      intersection = next;
    });

    return intersection;
  }

  _maybePinUserExerciseDisplayName(exerciseDisplayName, ctx) {
    const source = ctx?.source ?? null;
    const isUserInitiated = source !== "auto-suggestion";

    if (!isUserInitiated) return;
    if (!exerciseDisplayName) return;

    const existing = new Set(this.userPinnedExerciseDisplayNames ?? []);
    if (existing.has(exerciseDisplayName)) return;
    this.userPinnedExerciseDisplayNames = [...existing, exerciseDisplayName];
  }

  _getPinnedExercisesVisibleInScope({ t, language, exclude } = {}) {
    const pinned = this.userPinnedExerciseDisplayNames ?? [];
    if (!pinned || pinned.length === 0) return [];

    const excludeSet = exclude ?? new Set();
    const hasBlocks = (this.selectedBlockIds?.length ?? 0) > 0;

    if (hasBlocks) {
      const intersection = this._getExercisesIntersectionAcrossBlocks(
        this.selectedBlocks,
        { t, language }
      );
      return pinned.filter(
        (name) => name && intersection.has(name) && !excludeSet.has(name)
      );
    }

    const all = new Set(this.getAllExerciseDisplayNames(t, language));
    return pinned.filter(
      (name) => name && all.has(name) && !excludeSet.has(name)
    );
  }

  getPinnedExercisesVisibleInScopePublic({ t, language, exclude } = {}) {
    return this._getPinnedExercisesVisibleInScope({ t, language, exclude });
  }

  _suggestExercisesFromBlocks(blocks, { t, language, exclude } = {}) {
    if (!blocks || blocks.length === 0) return [];

    const stats = blocks.map((b) =>
      this._collectBlockExerciseStats(b, { t, language })
    );

    const allHaveSlots = stats.every((s) => s.hasSlot);

    // Primary rule: if every selected block has protocol-slot exercises, only
    // suggest exercises that appear with a protocol slot in all blocks.
    let intersection = null;
    if (allHaveSlots) {
      intersection = new Set(stats[0].slotNames ?? []);
      stats.slice(1).forEach((s) => {
        const next = new Set();
        (s.slotNames ?? []).forEach((name) => {
          if (intersection.has(name)) next.add(name);
        });
        intersection = next;
      });
    } else {
      // Fallback: intersection of any exercise with data across blocks.
      intersection = new Set(stats[0].allNames ?? []);
      stats.slice(1).forEach((s) => {
        const next = new Set();
        (s.allNames ?? []).forEach((name) => {
          if (intersection.has(name)) next.add(name);
        });
        intersection = next;
      });
    }

    if (!intersection || intersection.size === 0) return [];

    const ranked = Array.from(intersection).map((name) => {
      const total = stats.reduce(
        (sum, s) => sum + (s.freq?.get?.(name) ?? 0),
        0
      );
      return { name, total };
    });

    ranked.sort((a, b) => {
      if (b.total !== a.total) return b.total - a.total;
      return String(a.name).localeCompare(String(b.name));
    });

    return ranked
      .map((r) => r.name)
      .filter((n) => n && !exclude?.has?.(n))
      .slice(0, 15);
  }

  getAvailableProtocolSlotsForExerciseDisplayName(
    exerciseDisplayName,
    { t, language } = {}
  ) {
    if (!exerciseDisplayName) return [];
    try {
      const occurrences = this.getOccurrences({
        t,
        language,
        exerciseDisplayNameFilter: new Set([exerciseDisplayName]),
      });

      const slotBlockMap = Object.create(null);
      const allSlotsSet = new Set();

      (occurrences ?? []).forEach((o) => {
        if (!o) return;
        const slot = o.protocolSlot ?? null;
        const blockId = o.blockId ?? null;

        if (slot != null) {
          allSlotsSet.add(slot);
          if (blockId) {
            if (!Object.prototype.hasOwnProperty.call(slotBlockMap, slot)) {
              slotBlockMap[slot] = new Set();
            }
            slotBlockMap[slot].add(blockId);
          }
        }
      });

      const uniqueSlots = Array.from(allSlotsSet);
      const selectedCount = this.selectedBlockIds.length;
      const isWeeksMode = selectedCount > 0;

      // If comparing multiple blocks, a slot must be present in ALL selected blocks
      // to be enabled.
      const shouldCheckSharing = isWeeksMode && selectedCount > 1;

      return uniqueSlots.map((slot) => {
        let disabled = false;
        if (shouldCheckSharing) {
          const blocksWithSlot = slotBlockMap[slot]?.size ?? 0;
          if (blocksWithSlot < selectedCount) {
            disabled = true;
          }
        }
        return { value: slot, disabled };
      });
    } catch (e) {
      console.warn("getAvailableProtocolSlotsForExerciseDisplayName failed", e);
      return [];
    }
  }

  hasTrackDisplayName(exerciseDisplayName) {
    if (!exerciseDisplayName) return false;
    return (this.tracks ?? []).some(
      (t) => (t?.exerciseDisplayName ?? "") === exerciseDisplayName
    );
  }

  getTrackByDisplayName(exerciseDisplayName) {
    if (!exerciseDisplayName) return null;
    return (
      (this.tracks ?? []).find(
        (t) => (t?.exerciseDisplayName ?? "") === exerciseDisplayName
      ) ?? null
    );
  }

  ensureTrackByDisplayName(exerciseDisplayName, ctx) {
    if (!exerciseDisplayName) return null;

    const existing = this.getTrackByDisplayName(exerciseDisplayName);
    if (existing?.id) return existing.id;

    this._maybePinUserExerciseDisplayName(exerciseDisplayName, ctx);

    // If we're in single-track mode (multi-block), selecting a different
    // exercise should swap the current track, not change blocks.
    if (this.maxTracksAllowed === 1 && (this.tracks?.length ?? 0) > 0) {
      return this._replaceTracksWithSingleDisplayName(
        exerciseDisplayName,
        ctx,
        "ensureTrack -> swapTrackDueToMultiBlock"
      );
    }

    if ((this.tracks?.length ?? 0) >= this.maxTracksAllowed) return null;

    const newId = uuidv4();
    this.tracks = [
      ...(this.tracks ?? []),
      {
        id: newId,
        exerciseDisplayName,
        slotMode: "any",
        protocolSlot: null,
      },
    ];
    this._logDebug("ensureTrack -> addTrack", ctx);
    return newId;
  }

  toggleTrackByDisplayName(exerciseDisplayName, ctx) {
    if (!exerciseDisplayName) return;

    this._maybePinUserExerciseDisplayName(exerciseDisplayName, ctx);

    const existing = (this.tracks ?? []).find(
      (t) => (t?.exerciseDisplayName ?? "") === exerciseDisplayName
    );
    if (existing?.id) {
      this.removeTrack(existing.id, ctx);
      return;
    }
    this.addTrackByDisplayName(exerciseDisplayName, ctx);
  }

  _enforceSelectedBlocksEligibleForTracks(ctx) {
    try {
      // Selection invariants changed:
      // Selecting exercises must never unselect blocks, and selecting blocks must
      // never unselect exercises. Eligibility is still computed (see
      // `getEligibleBlockIdsForCurrentTracks`) for UI hints/filtering, but we do
      // not auto-mutate `selectedBlockIds` anymore.
      return;
    } catch (e) {
      console.warn("_enforceSelectedBlocksEligibleForTracks failed", e);
    }
  }

  getEligibleBlockIdsForCurrentTracks({ t, language } = {}) {
    const trackList = Array.isArray(this.tracks) ? this.tracks : [];
    if (trackList.length === 0) {
      return new Set(
        (this.availableBlocks ?? []).map((b) => b?.id).filter(Boolean)
      );
    }

    const names = trackList
      .map((tr) => tr?.exerciseDisplayName)
      .filter(Boolean);
    if (names.length === 0) {
      return new Set(
        (this.availableBlocks ?? []).map((b) => b?.id).filter(Boolean)
      );
    }

    // IMPORTANT: Block eligibility should not be influenced by the current
    // selection scope (selected blocks / week range / date range). We want to
    // know which blocks *could* be selected given the tracks.
    const getExerciseDisplayName = (exercise) => {
      try {
        if (typeof exercise?.getName === "function") {
          return exercise.getName(t, language);
        }
      } catch (e) {
        // ignore
      }
      return String(exercise?.name ?? "");
    };

    const blockHasTrack = (block, tr) => {
      const targetName = tr?.exerciseDisplayName ?? "";
      if (!targetName) return false;

      const requiredMode = tr?.slotMode ?? "any";
      const requiredSlot = tr?.protocolSlot ?? null;

      const workouts = Array.isArray(block?.workouts) ? block.workouts : [];
      for (const workout of workouts) {
        const exercises = Array.isArray(workout?.exercises)
          ? workout.exercises
          : [];
        for (const ex of exercises) {
          const name = getExerciseDisplayName(ex);
          if (name !== targetName) continue;

          if (requiredMode !== "exact") {
            return true;
          }
          if (!requiredSlot) {
            return false;
          }
          if ((ex?.protocolSlot ?? null) === requiredSlot) {
            return true;
          }
        }
      }
      return false;
    };

    const eligible = new Set();
    (this.availableBlocks ?? []).forEach((b) => {
      const blockId = b?.id;
      if (!blockId) return;

      const ok = trackList.every((tr) => blockHasTrack(b, tr));

      if (ok) eligible.add(blockId);
    });

    return eligible;
  }

  getAllExerciseDisplayNames(t, language) {
    const allNames = new Set();
    this.availableBlocks.forEach((b) => {
      if (!b?.getAllExercisesNames) return;
      try {
        (b.getAllExercisesNames(t, language) ?? []).forEach((name) => {
          if (name) allNames.add(name);
        });
      } catch (e) {
        // ignore broken blocks
      }
    });
    return Array.from(allNames);
  }

  toggleBlock(blockId, ctx) {
    if (!blockId) return;
    if (this.selectedBlockIds.includes(blockId)) {
      this.removeBlock(blockId, ctx);
    } else {
      this.addBlock(blockId, ctx);
    }
  }

  addBlock(blockId, ctx) {
    if (!blockId) return;

    // In multi-track mode we allow only a single selected block.
    // Selecting another block should swap (unselect previous, select new).
    if (this.maxBlocksAllowed === 1) {
      if (this.selectedBlockIds.includes(blockId)) return;
      this._setSingleSelectedBlockId(
        blockId,
        ctx,
        "addBlock -> swapBlockDueToMultiTrack"
      );
      return;
    }

    if (this.selectedBlockIds.includes(blockId)) return;
    if (this.selectedBlockIds.length >= this.maxBlocksAllowed) return;

    this.selectedBlockIds = [...this.selectedBlockIds, blockId];
    this.startWeek = 1;
    this.endWeek = this.getMaxWeeksAcrossSelectedBlocks();
    this._logDebug("addBlock", ctx);
  }

  removeBlock(blockId, ctx) {
    if (!blockId) return;
    this.selectedBlockIds = this.selectedBlockIds.filter(
      (id) => id !== blockId
    );
    if (this.selectedBlockIds.length === 0) {
      // Switched back to time mode.
      // IMPORTANT: do not reset startDate/endDate here.
      // Users may have selected a time interval (preset or custom), and
      // toggling blocks on/off should preserve that selection.
      this._logDebug("removeBlock -> switchToTimeMode", ctx);
      return;
    }
    this.endWeek = this.getMaxWeeksAcrossSelectedBlocks();
    this._logDebug("removeBlock", ctx);
  }

  addTrackByDisplayName(exerciseDisplayName, ctx) {
    if (!exerciseDisplayName) return;

    const alreadyExists = (this.tracks ?? []).some(
      (t) => (t?.exerciseDisplayName ?? "") === exerciseDisplayName
    );
    if (alreadyExists) return;

    // In multi-block mode we allow only a single selected track.
    // Selecting another exercise should swap (unselect previous, select new).
    if (this.maxTracksAllowed === 1 && (this.tracks?.length ?? 0) > 0) {
      this._replaceTracksWithSingleDisplayName(
        exerciseDisplayName,
        ctx,
        "addTrack -> swapTrackDueToMultiBlock"
      );
      this._enforceSelectedBlocksEligibleForTracks(ctx);
      return;
    }

    if (this.tracks.length >= this.maxTracksAllowed) return;

    this._maybePinUserExerciseDisplayName(exerciseDisplayName, ctx);

    this.tracks = [
      ...this.tracks,
      {
        id: uuidv4(),
        exerciseDisplayName,
        slotMode: "any",
        protocolSlot: null,
      },
    ];

    this._logDebug("addTrack", ctx);
    this._enforceSelectedBlocksEligibleForTracks(ctx);
  }

  addTrack(track) {
    // convenience for later; today we only support displayName string
    if (typeof track === "string") {
      this.addTrackByDisplayName(track);
      return;
    }
    if (track?.exerciseDisplayName) {
      this.addTrackByDisplayName(track.exerciseDisplayName);
    }
  }

  removeTrack(trackId, ctx) {
    if (!trackId) return;
    this.tracks = this.tracks.filter((t) => t.id !== trackId);
    this._logDebug("removeTrack", ctx);
    this._enforceSelectedBlocksEligibleForTracks(ctx);
  }

  setTrackSlot(trackId, slot, ctx) {
    if (!trackId) return;
    const slotMode = slot?.slotMode ?? slot?.mode;
    const protocolSlot = slot?.protocolSlot ?? null;

    this.tracks = this.tracks.map((t) => {
      if (t.id !== trackId) return t;
      return {
        ...t,
        slotMode: slotMode ?? t.slotMode ?? "any",
        protocolSlot: slotMode === "exact" ? protocolSlot : null,
      };
    });

    this._logDebug("setTrackSlot", ctx);
    this._enforceSelectedBlocksEligibleForTracks(ctx);
  }

  buildQuery() {
    // TODO: implement
    return null;
  }

  buildDataset() {
    // TODO: implement (could live here instead of pure builder functions)
    return null;
  }

  /**
   * Collects a flat list of all exercise occurrences within the current scope.
   * This is intentionally a store method (vs a pure builder) to keep iteration
   * close to the current data model while we stabilize requirements.
   */
  getOccurrences({ t, language, exerciseDisplayNameFilter } = {}) {
    const occurrences = [];

    const filter = exerciseDisplayNameFilter ?? null;
    const filterSet =
      filter && filter instanceof Set
        ? filter
        : Array.isArray(filter)
        ? new Set(filter.filter(Boolean))
        : null;

    const coerceDate = (value) => {
      if (!value) return null;
      if (value instanceof Date) return value;
      if (typeof value?.toDate === "function") {
        try {
          return value.toDate();
        } catch (e) {
          return null;
        }
      }
      const d = new Date(value);
      return isNaN(d.getTime()) ? null : d;
    };

    const getWorkoutDateMs = ({ block, workout, week }) => {
      const d = coerceDate(workout?.date);
      if (d) return d.getTime();

      // Week-mode blocks can be scheduled via calendarStartDate. If workout.date is
      // absent, derive a stable timestamp at Monday 12:00 for that block week.
      const start = coerceDate(block?.calendarStartDate);
      if (!start) return null;
      const w = typeof week === "number" && !isNaN(week) ? week : null;
      if (!w || w < 1) return null;
      const monday = getMondayOfWeek(start, true);
      const derived = new Date(monday);
      derived.setDate(monday.getDate() + (w - 1) * 7);
      return derived.getTime();
    };

    const selected = new Set(this.selectedBlockIds);
    const hasBlockFilter = selected.size > 0;

    const blocksToScan = hasBlockFilter
      ? this.availableBlocks.filter((b) => selected.has(b?.id))
      : this.availableBlocks;

    const startMs = coerceDate(this.startDate)?.getTime?.() ?? null;
    const endMs = coerceDate(this.endDate)?.getTime?.() ?? null;

    blocksToScan.forEach((block) => {
      const blockId = block?.id ?? null;
      const workouts = Array.isArray(block?.workouts) ? block.workouts : [];

      workouts.forEach((workout) => {
        const workoutId = workout?.id ?? null;
        const weekRaw = workout?.week;
        const week = typeof weekRaw === "number" ? weekRaw : Number(weekRaw);
        const hasValidWeek = typeof week === "number" && !isNaN(week);

        if (this.mode === "weeks") {
          const w = hasValidWeek ? week : 0;
          if (w < this.startWeek || w > this.endWeek) return;
        } else {
          // time mode
          const dateMs = getWorkoutDateMs({ block, workout, week });
          if (dateMs == null) return;
          if (startMs != null && dateMs < startMs) return;
          if (endMs != null && dateMs > endMs) return;
        }

        const dateMs = getWorkoutDateMs({ block, workout, week });
        const exercises = Array.isArray(workout?.exercises)
          ? workout.exercises
          : [];

        exercises.forEach((exercise) => {
          const exerciseId = exercise?.id ?? null;

          let exerciseDisplayName = "";
          try {
            if (typeof exercise?.getName === "function") {
              exerciseDisplayName = exercise.getName(t, language);
            } else {
              exerciseDisplayName = String(exercise?.name ?? "");
            }
          } catch (e) {
            exerciseDisplayName = String(exercise?.name ?? "");
          }

          if (filterSet && !filterSet.has(exerciseDisplayName)) return;

          const series = Array.isArray(exercise?.series) ? exercise.series : [];
          const setsCount = series.length;

          // Match legacy table: each set on its own line.
          let seriesText = "";
          try {
            const rirOrRpe = exercise?.getRirOrRpe ?? false;
            seriesText = series
              .slice()
              .sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0))
              .map(
                (s) =>
                  `${formatNumber2Dec(s?.weight)} x ${formatNumber2Dec(
                    s?.reps
                  )} ${rirOrRpe ? "RIR" : "@"}${formatNumber2Dec(s?.rpe)}`
              )
              .join("\n");
          } catch (e) {
            seriesText = "";
          }

          // Match legacy metrics used by the Statistics tables.
          const e1rm =
            typeof exercise?.getMaxErm === "number" && exercise.getMaxErm > 0
              ? exercise.getMaxErm
              : 0;
          const stressIndex =
            typeof exercise?.getStressIndex === "number" &&
            exercise.getStressIndex > 0
              ? exercise.getStressIndex
              : 0;
          const tonnage =
            typeof exercise?.getTonnage === "number" && exercise.getTonnage > 0
              ? exercise.getTonnage
              : 0;
          const averageIntensity =
            typeof exercise?.getAverageIntensity === "number" &&
            exercise.getAverageIntensity > 0
              ? exercise.getAverageIntensity
              : 0;
          const numberOfLifts =
            typeof exercise?.getNumberLifts === "number" &&
            exercise.getNumberLifts > 0
              ? exercise.getNumberLifts
              : 0;
          const numberOfSets =
            typeof exercise?.getNumberSeries === "number" &&
            exercise.getNumberSeries > 0
              ? exercise.getNumberSeries
              : 0;
          const exertionLoad =
            typeof exercise?.getExertionLoad === "number" &&
            exercise.getExertionLoad > 0
              ? exercise.getExertionLoad
              : 0;

          occurrences.push({
            exerciseDisplayName,
            protocolSlot: exercise?.protocolSlot ?? null,
            blockId,
            workoutId,
            exerciseId,
            week: hasValidWeek ? week : null,
            dateMs,
            setsCount,
            seriesText,
            e1rm,
            stressIndex,
            tonnage,
            averageIntensity,
            numberOfLifts,
            numberOfSets,
            exertionLoad,
          });
        });
      });
    });

    return occurrences;
  }

  /**
   * Converts occurrences into per-track points (still no chart/table yet).
   * Matching is by exact exerciseDisplayName.
   *
   * Slot filter:
   * - any: keep every matching occurrence
   * - exact: keep only occurrences with matching protocolSlot
   *
   * Adds `occurrenceIndexInX` per x group to disambiguate duplicates.
   */
  getTrackPoints(trackId, { t, language } = {}) {
    if (!trackId) return [];
    const track = (this.tracks ?? []).find((tr) => tr?.id === trackId) ?? null;
    if (!track?.exerciseDisplayName) return [];

    const occurrences = this.getOccurrences({
      t,
      language,
      exerciseDisplayNameFilter: new Set([track.exerciseDisplayName]),
    });
    const matchName = track.exerciseDisplayName;

    let filtered = occurrences.filter(
      (o) => (o?.exerciseDisplayName ?? "") === matchName
    );

    const slotMode = track.slotMode ?? "any";
    const protocolSlot = track.protocolSlot ?? null;
    if (slotMode === "exact") {
      if (!protocolSlot) {
        filtered = [];
      } else {
        filtered = filtered.filter(
          (o) => (o?.protocolSlot ?? null) === protocolSlot
        );
      }
    }

    return this._pointsFromFilteredOccurrences(filtered);
  }

  _getTrackPointsFromOccurrences(track, occurrences) {
    if (!track?.exerciseDisplayName) return [];
    const matchName = track.exerciseDisplayName;

    let filtered = (occurrences ?? []).filter(
      (o) => (o?.exerciseDisplayName ?? "") === matchName
    );

    const slotMode = track.slotMode ?? "any";
    const protocolSlot = track.protocolSlot ?? null;
    if (slotMode === "exact") {
      if (!protocolSlot) {
        filtered = [];
      } else {
        filtered = filtered.filter(
          (o) => (o?.protocolSlot ?? null) === protocolSlot
        );
      }
    }

    return this._pointsFromFilteredOccurrences(filtered);
  }

  _pointsFromFilteredOccurrences(filtered, colorOptions) {
    const xForOccurrence = (o) => {
      if (this.mode === "weeks") return o?.week ?? null;
      return o?.dateMs ?? null;
    };

    const byX = new Map();
    (filtered ?? []).forEach((o) => {
      const x = xForOccurrence(o);
      if (x == null) return;
      const key = String(x);
      const arr = byX.get(key) ?? [];
      arr.push(o);
      byX.set(key, arr);
    });

    const points = [];
    for (const [key, arr] of byX.entries()) {
      const duplicateCountInX = arr.length;
      arr.forEach((o, idx) => {
        const exerciseDisplayName = o?.exerciseDisplayName ?? "";
        const protocolSlot = o?.protocolSlot ?? null;
        const color = this.getColorForExerciseAndSlot(
          exerciseDisplayName,
          protocolSlot,
          colorOptions
        );
        points.push({
          x: this.mode === "weeks" ? o?.week ?? null : o?.dateMs ?? null,
          occurrenceIndexInX: idx,
          duplicateCountInX,
          blockId: o?.blockId ?? null,
          workoutId: o?.workoutId ?? null,
          exerciseId: o?.exerciseId ?? null,
          exerciseDisplayName,
          protocolSlot,
          color,
          week: o?.week ?? null,
          dateMs: o?.dateMs ?? null,
          setsCount: o?.setsCount ?? 0,
          seriesText: o?.seriesText ?? "",
          e1rm: o?.e1rm ?? 0,
          stressIndex: o?.stressIndex ?? 0,
          tonnage: o?.tonnage ?? 0,
          averageIntensity: o?.averageIntensity ?? 0,
          numberOfLifts: o?.numberOfLifts ?? 0,
          numberOfSets: o?.numberOfSets ?? 0,
          exertionLoad: o?.exertionLoad ?? 0,
        });
      });
    }

    // Stable ordering for table/chart consumers.
    points.sort((a, b) => {
      const ax = a?.x ?? 0;
      const bx = b?.x ?? 0;
      if (ax !== bx) return ax - bx;
      return (a?.occurrenceIndexInX ?? 0) - (b?.occurrenceIndexInX ?? 0);
    });

    return points;
  }

  /**
   * Chart-ready series definitions derived from current selection state.
   *
   * Goal: one entry per "legend item" we might render in the chart.
   * - If a track is `slotMode === "exact"`, series is that (exercise, slot).
   * - If a track is `slotMode === "any"` and multiple slots exist, series splits into (exercise, slot) entries.
   * - Colors are stable per (exerciseDisplayName, protocolSlot).
   */
  getChartSeriesModel({ t, language } = {}) {
    const tracks = Array.isArray(this.tracks) ? this.tracks : [];
    if (!t || !language || tracks.length === 0) {
      return {
        mode: this.mode,
        xAxisType: this.mode === "weeks" ? "week" : "time",
        series: [],
      };
    }

    const trackNames = tracks
      .map((tr) => tr?.exerciseDisplayName)
      .filter(Boolean);

    const occurrences = this.getOccurrences({
      t,
      language,
      exerciseDisplayNameFilter: trackNames.length ? new Set(trackNames) : null,
    });

    const series = [];

    // Multi-block weeks comparisons should color by block identity, except when
    // a single block splits into multiple protocol slots (then use slot colors).
    const isMultiBlockWeeks =
      this.mode === "weeks" && (this.selectedBlockIds?.length ?? 0) > 1;

    if (isMultiBlockWeeks) {
      const blocksInSelectionOrder = (this.selectedBlockIds ?? [])
        .map((id) => this.getBlockById(id))
        .filter(Boolean);

      tracks.forEach((tr) => {
        const exerciseDisplayName = tr?.exerciseDisplayName ?? "";
        if (!exerciseDisplayName) return;

        const base = (occurrences ?? []).filter(
          (o) => (o?.exerciseDisplayName ?? "") === exerciseDisplayName
        );

        const slotMode = tr?.slotMode ?? "any";
        const selectedSlot = tr?.protocolSlot ?? null;

        blocksInSelectionOrder.forEach((block) => {
          const blockId = block?.id ?? null;
          const blockName = block?.name ?? "";
          if (!blockId) return;

          let blockPoints = (base ?? []).filter((o) => o?.blockId === blockId);
          if (blockPoints.length === 0) return;

          const blockColor = this.getColorForBlock(blockId);

          // Exact slot → one series per block using the block color.
          if (slotMode === "exact") {
            if (!selectedSlot) return;
            blockPoints = blockPoints.filter(
              (o) => (o?.protocolSlot ?? null) === selectedSlot
            );
            if (blockPoints.length === 0) return;

            const points = this._pointsFromFilteredOccurrences(blockPoints).map(
              (p) => ({ ...p, color: blockColor })
            );

            series.push({
              seriesKey: `${blockId}::${exerciseDisplayName}::${String(
                selectedSlot ?? "none"
              )}`,
              trackId: tr?.id ?? null,
              blockId,
              exerciseDisplayName,
              protocolSlot: selectedSlot,
              label: blockName
                ? `${blockName} - ${this._formatSeriesLabel({
                    t,
                    exerciseDisplayName,
                    protocolSlot: selectedSlot,
                  })}`
                : this._formatSeriesLabel({
                    t,
                    exerciseDisplayName,
                    protocolSlot: selectedSlot,
                  }),
              color: blockColor,
              points,
            });
            return;
          }

          // slotMode === "any" → optionally split per slot within the block.
          const slotsInBlock = Array.from(
            new Set(
              (blockPoints ?? [])
                .map((o) => o?.protocolSlot ?? null)
                .filter((s) => s != null)
            )
          ).sort((a, b) => String(a).localeCompare(String(b)));

          if (slotsInBlock.length > 1) {
            slotsInBlock.forEach((slotKey) => {
              const slotPoints = (blockPoints ?? []).filter(
                (o) => (o?.protocolSlot ?? null) === slotKey
              );
              if (slotPoints.length === 0) return;

              const seriesColor = this.getColorForExerciseAndSlot(
                exerciseDisplayName,
                slotKey,
                { forceSlotVariant: true, identitySalt: blockId }
              );

              const points = this._pointsFromFilteredOccurrences(slotPoints, {
                forceSlotVariant: true,
                identitySalt: blockId,
              }).map((p) => ({ ...p, color: seriesColor }));

              series.push({
                seriesKey: `${blockId}::${exerciseDisplayName}::${String(
                  slotKey ?? "none"
                )}`,
                trackId: tr?.id ?? null,
                blockId,
                exerciseDisplayName,
                protocolSlot: slotKey,
                label: blockName
                  ? `${blockName} - ${this._formatSeriesLabel({
                      t,
                      exerciseDisplayName,
                      protocolSlot: slotKey,
                    })}`
                  : this._formatSeriesLabel({
                      t,
                      exerciseDisplayName,
                      protocolSlot: slotKey,
                    }),
                color: seriesColor,
                points,
              });
            });
            return;
          }

          // 0 or 1 slot present in this block → one series colored by block.
          const impliedSlot =
            slotsInBlock.length === 1 ? slotsInBlock[0] : null;
          const points = this._pointsFromFilteredOccurrences(blockPoints).map(
            (p) => ({ ...p, color: blockColor })
          );

          series.push({
            seriesKey: `${blockId}::${exerciseDisplayName}::${String(
              impliedSlot ?? "none"
            )}`,
            trackId: tr?.id ?? null,
            blockId,
            exerciseDisplayName,
            protocolSlot: impliedSlot,
            label: blockName
              ? `${blockName} - ${this._formatSeriesLabel({
                  t,
                  exerciseDisplayName,
                  protocolSlot: impliedSlot,
                })}`
              : this._formatSeriesLabel({
                  t,
                  exerciseDisplayName,
                  protocolSlot: impliedSlot,
                }),
            color: blockColor,
            points,
          });
        });
      });

      return {
        mode: this.mode,
        xAxisType: "week",
        series,
      };
    }

    tracks.forEach((tr) => {
      const exerciseDisplayName = tr?.exerciseDisplayName ?? "";
      if (!exerciseDisplayName) return;

      const base = (occurrences ?? []).filter(
        (o) => (o?.exerciseDisplayName ?? "") === exerciseDisplayName
      );

      const slotMode = tr?.slotMode ?? "any";
      const selectedSlot = tr?.protocolSlot ?? null;

      if (slotMode === "exact") {
        const filtered = selectedSlot
          ? base.filter((o) => (o?.protocolSlot ?? null) === selectedSlot)
          : [];
        const points = this._pointsFromFilteredOccurrences(filtered);
        series.push({
          seriesKey: `${exerciseDisplayName}::${String(
            selectedSlot ?? "none"
          )}`,
          trackId: tr?.id ?? null,
          exerciseDisplayName,
          protocolSlot: selectedSlot,
          label: this._formatSeriesLabel({
            t,
            exerciseDisplayName,
            protocolSlot: selectedSlot,
          }),
          color: this.getColorForExerciseAndSlot(
            exerciseDisplayName,
            selectedSlot
          ),
          points,
        });
        return;
      }

      // slotMode === "any"
      const slotsInScope = Array.from(
        new Set(
          (base ?? [])
            .map((o) => o?.protocolSlot ?? null)
            .filter((s) => s != null)
        )
      );

      if (slotsInScope.length > 1) {
        slotsInScope
          .sort((a, b) => String(a).localeCompare(String(b)))
          .forEach((slotKey) => {
            const filtered = base.filter(
              (o) => (o?.protocolSlot ?? null) === slotKey
            );
            const color = this.getColorForExerciseAndSlot(
              exerciseDisplayName,
              slotKey,
              { forceSlotVariant: true }
            );
            const points = this._pointsFromFilteredOccurrences(filtered, {
              forceSlotVariant: true,
            }).map((p) => ({ ...p, color }));
            series.push({
              seriesKey: `${exerciseDisplayName}::${String(slotKey ?? "none")}`,
              trackId: tr?.id ?? null,
              exerciseDisplayName,
              protocolSlot: slotKey,
              label: this._formatSeriesLabel({
                t,
                exerciseDisplayName,
                protocolSlot: slotKey,
              }),
              color,
              points,
            });
          });
        return;
      }

      // 0 or 1 slot in scope => keep a single series (matches current UI behavior)
      // IMPORTANT: In weeks mode, the table currently uses the exercise-only
      // color when the user selects "All slots" and the exercise only has one
      // slot in scope (it does not treat that as an explicit slot selection).
      // To keep chart/legend/table colors identical, mirror that behavior here.
      const impliedSlot =
        this.mode === "weeks"
          ? null
          : slotsInScope.length === 1
          ? slotsInScope[0]
          : null;
      const points = this._pointsFromFilteredOccurrences(base);
      series.push({
        seriesKey: `${exerciseDisplayName}::${String(impliedSlot ?? "none")}`,
        trackId: tr?.id ?? null,
        exerciseDisplayName,
        protocolSlot: impliedSlot,
        label: this._formatSeriesLabel({
          t,
          exerciseDisplayName,
          protocolSlot: impliedSlot,
        }),
        color: this.getColorForExerciseAndSlot(
          exerciseDisplayName,
          impliedSlot
        ),
        points,
      });
    });

    return {
      mode: this.mode,
      xAxisType: this.mode === "weeks" ? "week" : "time",
      series,
    };
  }

  _formatLabel(value) {
    const n = typeof value === "number" ? value : Number(value);
    if (!isFinite(n)) return "0";
    const threshold = 0.05;
    return Math.abs(n % 1) < threshold ? n.toFixed(0) : n.toFixed(1);
  }

  /**
   * Rows for the first "debug table" UI.
   * This defines exactly what the table renders.
   */
  getDebugTableRows({ t, language } = {}) {
    // Backwards-compat wrapper (older table). Keep returning time-style rows.
    return this.getDebugTableModel({ t, language })?.rows ?? [];
  }

  /**
   * Table model shaped to follow the legacy pattern:
   * - Weeks mode (block selected): Exercise | W1 | W2 | ...
   * - Time mode (no block selected): Date | Exercise | Sets | Metrics
   *
   * The UI should render exactly this model.
   */
  getDebugTableModel({ t, language } = {}) {
    const tracks = Array.isArray(this.tracks) ? this.tracks : [];
    if (!t || !language || tracks.length === 0) {
      return { columns: [], rows: [], mode: this.mode };
    }

    const formatTrackTitle = (tr) => {
      const base = tr?.exerciseDisplayName ?? "";
      const slotMode = tr?.slotMode ?? "any";
      const protocolSlot = tr?.protocolSlot ?? null;
      if (slotMode === "exact" && protocolSlot) {
        return `${base} (${t(protocolSlot)})`;
      }
      return base;
    };

    const formatTrackTitleForSlot = (tr, protocolSlot) => {
      const base = tr?.exerciseDisplayName ?? "";
      if (protocolSlot) return `${base} (${t(protocolSlot)})`;
      return base;
    };

    const trackNames = tracks
      .map((tr) => tr?.exerciseDisplayName)
      .filter(Boolean);

    const occurrences = this.getOccurrences({
      t,
      language,
      exerciseDisplayNameFilter: trackNames.length ? new Set(trackNames) : null,
    });

    // Only include the "third metric" (bar layer) in the table when the
    // bar layer is actually allowed by the same constraints as the chart.
    const chartModel = this.getChartSeriesModel({ t, language });
    const seriesCount = Array.isArray(chartModel?.series)
      ? chartModel.series.length
      : 0;

    const canShowBarForTable =
      (this.selectedBlockIds?.length ?? 0) <= 1 &&
      (tracks?.length ?? 0) <= 1 &&
      // If slotMode=any splits into multiple series, bar should be disallowed.
      (seriesCount === 1 || seriesCount === 0);

    const metricOptions = this.metricOptions ?? [];
    const metricByType = this.resolvedMetricByType ?? {};

    const pickOption = (key) =>
      (metricOptions ?? []).find((m) => m?.key === key) ?? null;

    const ordered = [
      pickOption(metricByType.line),
      pickOption(metricByType.area),
      canShowBarForTable ? pickOption(metricByType.bar) : null,
    ]
      .filter(Boolean)
      .filter((m) => m?.key !== "none");

    // Unique by metric key, preserve order (line -> area -> bar)
    const seen = new Set();
    const metrics = ordered.filter((m) => {
      const k = m?.key;
      if (!k || seen.has(k)) return false;
      seen.add(k);
      return true;
    });

    if (this.mode === "weeks") {
      const start = this.startWeek ?? 1;
      const end = this.endWeek ?? start;

      const isMultiBlock = this.selectedBlockIds.length > 1;

      const weekKeys = [];
      const columns = [
        {
          key: isMultiBlock ? "blockHeader-string" : "exerciseHeader-string",
          title: isMultiBlock
            ? t("blockHeader-string")
            : t("exerciseHeader-string"),
          minWidth: 220,
          flex: 1,
        },
        ...(isMultiBlock
          ? [
              {
                key: "exerciseHeader-string",
                title: t("exerciseHeader-string"),
                minWidth: 220,
                flex: 1,
              },
            ]
          : []),
        {
          key: "metricsHeader-string",
          title: t("metricsHeader-string"),
          minWidth: 140,
          flex: 1,
        },
      ];
      for (let w = start; w <= end; w++) {
        const wk = t("wOfExercise-string", { week: w });
        weekKeys.push(wk);
        columns.push({ key: wk, title: wk, flex: 1, minWidth: 140 });
      }

      const makeGroupRows = ({ points }) => {
        const byWeek = new Map();
        (points ?? []).forEach((p) => {
          const week = p?.week;
          if (typeof week !== "number") return;
          const arr = byWeek.get(week) ?? [];
          arr.push(p);
          byWeek.set(week, arr);
        });

        const setsRow = {
          __label: t("sets-string"),
          __isMetric: false,
          __metricKey: null,
        };
        weekKeys.forEach((wk) => {
          setsRow[wk] = "";
        });

        for (let w = start; w <= end; w++) {
          const wk = t("wOfExercise-string", { week: w });
          const pts = (byWeek.get(w) ?? [])
            .slice()
            .sort(
              (a, b) =>
                (a?.occurrenceIndexInX ?? 0) - (b?.occurrenceIndexInX ?? 0)
            );

          if (pts.length === 0) {
            setsRow[wk] = "";
          } else {
            // Stack duplicate occurrences one below the other (legacy-style).
            setsRow[wk] = pts
              .map((p) => p?.seriesText ?? "")
              .filter(Boolean)
              .join("\n\n");
          }
        }

        const metricRows = (metrics ?? []).map((metric) => {
          const metricKey = metric?.key ?? "";
          const label = metric?.labelKey
            ? t(metric.labelKey)
            : metric?.label ?? String(metricKey);
          const row = {
            __label: label,
            __isMetric: true,
            __metricKey: metricKey,
          };

          for (let w = start; w <= end; w++) {
            const wk = t("wOfExercise-string", { week: w });
            const pts = (byWeek.get(w) ?? [])
              .slice()
              .sort(
                (a, b) =>
                  (a?.occurrenceIndexInX ?? 0) - (b?.occurrenceIndexInX ?? 0)
              );
            if (pts.length === 0) {
              row[wk] = "-";
              continue;
            }

            const isMultiOccurrence = pts.length > 1;
            const formattedValues = pts.map((p) => {
              const rawValue = getMetricValue(p, metricKey);
              const valueNumber =
                typeof rawValue === "number" ? rawValue : Number(rawValue);

              if (valueNumber > 0) return this._formatLabel(valueNumber);
              // When multiple occurrences, show zero explicitly instead of dash.
              if (isMultiOccurrence) return "0";
              return "-";
            });

            const hasNonDash = formattedValues.some((v) => v !== "-");
            row[wk] = hasNonDash ? formattedValues.join(" · ") : "-";
          }

          return row;
        });

        return [setsRow, ...metricRows];
      };

      let groups = [];

      if (isMultiBlock) {
        // Multi-block implies single track (enforced elsewhere).
        // Table should show one group per block, and when "All Slots" is selected, one group per slot within that block.
        const tr = tracks[0] ?? null;
        const allPoints = this._getTrackPointsFromOccurrences(tr, occurrences);

        const slotMode = tr?.slotMode ?? "any";

        const blocksInSelectionOrder = (this.selectedBlockIds ?? [])
          .map((id) => this.getBlockById(id))
          .filter(Boolean);

        groups = blocksInSelectionOrder.flatMap((block) => {
          const blockId = block?.id ?? null;
          const pointsForBlock = (allPoints ?? []).filter(
            (p) => p?.blockId === blockId
          );

          if (slotMode !== "exact") {
            const slotKeys = Array.from(
              new Set(
                (pointsForBlock ?? [])
                  .map((p) => p?.protocolSlot ?? null)
                  .filter((s) => s != null)
              )
            ).sort();

            if (slotKeys.length > 1) {
              return slotKeys.map((slotKey) => {
                const slotPoints = (pointsForBlock ?? []).filter(
                  (p) => (p?.protocolSlot ?? null) === slotKey
                );
                return {
                  key: `${blockId ?? block?.name ?? "block"}:${slotKey}`,
                  title: block?.name ?? "",
                  __blockId: blockId,
                  __blockSeriesColor: this.getColorForBlock(blockId),
                  __isSlotSplitWithinBlock: true,
                  exerciseTitle: formatTrackTitleForSlot(tr, slotKey),
                  __exerciseDisplayName: tr?.exerciseDisplayName ?? "",
                  __protocolSlot: slotKey,
                  __seriesColor: this.getColorForExerciseAndSlot(
                    tr?.exerciseDisplayName ?? "",
                    slotKey,
                    { forceSlotVariant: true, identitySalt: blockId }
                  ),
                  rows: makeGroupRows({ points: slotPoints }),
                };
              });
            }
          }

          const singleSlot =
            slotMode === "exact" && tr?.protocolSlot
              ? tr.protocolSlot
              : pointsForBlock?.[0]?.protocolSlot ?? null;

          return [
            {
              key: blockId ?? block?.name ?? "",
              title: block?.name ?? "",
              __blockId: blockId,
              __blockSeriesColor: this.getColorForBlock(blockId),
              __isSlotSplitWithinBlock: false,
              exerciseTitle:
                slotMode === "exact"
                  ? formatTrackTitle(tr)
                  : formatTrackTitleForSlot(tr, singleSlot),
              __exerciseDisplayName: tr?.exerciseDisplayName ?? "",
              __protocolSlot:
                slotMode === "exact" ? tr?.protocolSlot ?? null : singleSlot,
              __seriesColor: this.getColorForExerciseAndSlot(
                tr?.exerciseDisplayName ?? "",
                slotMode === "exact" ? tr?.protocolSlot ?? null : singleSlot,
                { identitySalt: blockId }
              ),
              rows: makeGroupRows({ points: pointsForBlock }),
            },
          ];
        });
      } else {
        // Single-block (or no explicit multi-block compare): keep legacy "exercise group" layout.
        groups = tracks.flatMap((tr) => {
          const points = this._getTrackPointsFromOccurrences(tr, occurrences);

          // If user selected "All Slots" (slotMode=any), split into one section per slot.
          const slotMode = tr?.slotMode ?? "any";
          if (slotMode !== "exact") {
            const slotKeys = Array.from(
              new Set(
                (points ?? [])
                  .map((p) => p?.protocolSlot ?? null)
                  .filter((s) => s != null)
              )
            ).sort();

            if (slotKeys.length > 1) {
              return slotKeys.map((slotKey) => {
                const slotPoints = (points ?? []).filter(
                  (p) => (p?.protocolSlot ?? null) === slotKey
                );
                return {
                  key: `${
                    tr?.id ?? tr?.exerciseDisplayName ?? "track"
                  }:${slotKey}`,
                  title: formatTrackTitleForSlot(tr, slotKey),
                  __exerciseDisplayName: tr?.exerciseDisplayName ?? "",
                  __protocolSlot: slotKey,
                  __seriesColor: this.getColorForExerciseAndSlot(
                    tr?.exerciseDisplayName ?? "",
                    slotKey,
                    { forceSlotVariant: true }
                  ),
                  rows: makeGroupRows({ points: slotPoints }),
                };
              });
            }
          }

          return [
            {
              key: tr?.exerciseDisplayName ?? tr?.id,
              title: formatTrackTitle(tr),
              __exerciseDisplayName: tr?.exerciseDisplayName ?? "",
              __protocolSlot:
                (tr?.slotMode ?? "any") === "exact"
                  ? tr?.protocolSlot ?? null
                  : null,
              __seriesColor: this.getColorForExerciseAndSlot(
                tr?.exerciseDisplayName ?? "",
                (tr?.slotMode ?? "any") === "exact"
                  ? tr?.protocolSlot ?? null
                  : null
              ),
              rows: makeGroupRows({ points }),
            },
          ];
        });
      }

      return { mode: this.mode, columns, groups };
    }

    // time mode (group by date like legacy DatesTable)
    const metricColumns = (metrics ?? []).map((metric) => ({
      key: metric?.key ?? "metric",
      title: metric?.labelKey
        ? t(metric.labelKey)
        : metric?.label ?? String(metric?.key ?? ""),
      flex: 1,
      minWidth: 130,
      isMetric: true,
      metricKey: metric?.key ?? "",
    }));

    const columns = [
      {
        key: "dateHeader-string",
        title: t("dateHeader-string"),
        width: 120,
      },
      {
        key: "exerciseHeader-string",
        title: t("exerciseHeader-string"),
        flex: 1,
        minWidth: 220,
      },
      {
        key: "setsHeader-string",
        title: t("setsHeader-string"),
        flex: 1,
        minWidth: 160,
      },
      ...metricColumns,
    ];

    const rowsFlat = [];
    tracks.forEach((tr) => {
      const points = this._getTrackPointsFromOccurrences(tr, occurrences);
      const slotMode = tr?.slotMode ?? "any";
      const slotsInTrack =
        slotMode !== "exact"
          ? Array.from(
              new Set(
                (points ?? [])
                  .map((p) => p?.protocolSlot ?? null)
                  .filter((s) => s != null)
              )
            )
          : [];
      const isSlotSplit = slotMode !== "exact" && slotsInTrack.length > 1;
      points.forEach((p) => {
        const dateObj = p?.dateMs ? new Date(p.dateMs) : null;
        const dateLabel = dateObj ? dateObj.toLocaleDateString() : "";

        const slotLabel = p?.protocolSlot ? ` (${t(p.protocolSlot)})` : "";
        const occurrenceLabel =
          (p?.duplicateCountInX ?? 0) > 1
            ? ` #${(p?.occurrenceIndexInX ?? 0) + 1}`
            : "";

        const metricValues = {};
        (metrics ?? []).forEach((metric) => {
          const metricKey = metric?.key ?? "";
          metricValues[metricKey] = getMetricValue(p, metricKey);
        });

        rowsFlat.push({
          __dateMs: p?.dateMs ?? 0,
          __dateLabel: dateLabel,
          __exerciseDisplayName: tr?.exerciseDisplayName ?? "",
          __protocolSlot: p?.protocolSlot ?? null,
          __seriesColor: this.getColorForExerciseAndSlot(
            tr?.exerciseDisplayName ?? "",
            p?.protocolSlot ?? null,
            {
              forceSlotVariant:
                isSlotSplit && (p?.protocolSlot ?? null) != null,
            }
          ),
          __occurrenceLabel: occurrenceLabel
            ? String(occurrenceLabel).trim()
            : "",
          "exerciseHeader-string": `${
            tr?.exerciseDisplayName ?? ""
          }${slotLabel}${occurrenceLabel}`,
          "setsHeader-string": p?.seriesText ?? "",
          ...metricValues,
        });
      });
    });

    rowsFlat.sort((a, b) => (b?.__dateMs ?? 0) - (a?.__dateMs ?? 0));

    const grouped = new Map();
    rowsFlat.forEach((r) => {
      const key = r.__dateLabel ?? "";
      const arr = grouped.get(key) ?? [];
      arr.push(r);
      grouped.set(key, arr);
    });

    const groups = Array.from(grouped.entries()).map(([dateLabel, rows]) => ({
      key: dateLabel,
      dateLabel,
      rows,
    }));

    return { mode: this.mode, columns, groups };
  }

  getMaxWeeksAcrossSelectedBlocks() {
    if (!this.client) return 1;
    const selected = new Set(this.selectedBlockIds);
    const blocks = Array.isArray(this.client.blocks) ? this.client.blocks : [];
    let maxWeeks = 1;
    blocks.forEach((b) => {
      if (!selected.has(b?.id)) return;
      const weeks = typeof b?.getWeeks === "number" ? b.getWeeks : b?.getWeeks;
      if (typeof weeks === "number" && weeks > maxWeeks) maxWeeks = weeks;
    });
    return maxWeeks;
  }

  get debugSnapshot() {
    return {
      mode: this.mode,
      selectedBlockIds: this.selectedBlockIds,
      tracks: this.tracks,
      range:
        this.mode === "weeks"
          ? { startWeek: this.startWeek, endWeek: this.endWeek }
          : {
              startDate: this.startDate?.toISOString?.() ?? this.startDate,
              endDate: this.endDate?.toISOString?.() ?? this.endDate,
            },
      limits: {
        maxBlocksAllowed: this.maxBlocksAllowed,
        maxTracksAllowed: this.maxTracksAllowed,
      },
      metrics: this.resolvedMetricByType,
    };
  }
}

export default ClientStatisticsStore;
